/**
 * Website Sales - Prospect management for website service sales
 *
 * Sales role can manage prospects interested in website builds.
 * One-time £120 commission per website sale.
 */
import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Globe,
  Plus,
  Phone,
  Mail,
  Building2,
  Search,
  Filter,
  X,
  ChevronRight,
  MessageSquare,
  Send,
  RefreshCw,
  ExternalLink,
  PoundSterling,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  getProspects,
  getProspect,
  createProspect,
  updateProspectStatus,
  addProspectNote,
  bulkImportProspects,
  type BulkImportResult,
} from '@/services/salesApi'
import type {
  ProspectListItem,
  ProspectDetail,
  ProspectStatus,
  ProspectService,
} from '@/types'

const STATUS_CONFIG: Record<ProspectStatus, { bg: string; text: string; label: string }> = {
  new: { bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'New' },
  contacted: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', label: 'Contacted' },
  warm: { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Warm' },
  in_discussion: { bg: 'bg-purple-500/10', text: 'text-purple-400', label: 'In Discussion' },
  won: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Won' },
  not_interested: { bg: 'bg-slate-500/10', text: 'text-slate-400', label: 'Not Interested' },
  lost: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Lost' },
}

export default function WebsiteProspects() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [prospects, setProspects] = useState<ProspectListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [selectedProspect, setSelectedProspect] = useState<ProspectDetail | null>(null)
  const [showDetailPanel, setShowDetailPanel] = useState(false)

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<ProspectStatus | 'all'>('all')

  // Form state
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    address: '',
    website: '',
    services: 'website' as ProspectService,
    socialMediaAdvertising: false,
    nextFollowUpDate: '',
    initialNote: '',
  })

  // Note form
  const [newNote, setNewNote] = useState('')
  const [addingNote, setAddingNote] = useState(false)

  // Bulk import
  const [showBulkImportModal, setShowBulkImportModal] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<BulkImportResult | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  // Handle prospect URL param
  useEffect(() => {
    const prospectId = searchParams.get('prospect')
    if (prospectId) {
      loadProspectDetail(parseInt(prospectId))
    }
  }, [searchParams])

  const fetchData = async () => {
    setLoading(true)
    try {
      const prospectsData = await getProspects({ services: 'website' })
      setProspects(prospectsData || [])
    } catch {
      toast.error('Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const loadProspectDetail = async (id: number) => {
    try {
      const detail = await getProspect(id)
      setSelectedProspect(detail)
      setShowDetailPanel(true)
    } catch {
      toast.error('Failed to load prospect details')
    }
  }

  // Filtered prospects
  const filteredProspects = useMemo(() => {
    return prospects.filter((p) => {
      if (filterStatus !== 'all' && p.status !== filterStatus) return false
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        return (
          p.companyName.toLowerCase().includes(q) ||
          p.contactName.toLowerCase().includes(q) ||
          p.contactPhone.includes(q)
        )
      }
      return true
    })
  }, [prospects, filterStatus, searchQuery])

  const handleCreateProspect = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await createProspect({
        companyName: formData.companyName,
        contactName: formData.contactName,
        contactPhone: formData.contactPhone,
        contactEmail: formData.contactEmail || undefined,
        address: formData.address || undefined,
        website: formData.website || undefined,
        services: formData.services,
        socialMediaAdvertising: formData.socialMediaAdvertising,
        nextFollowUpDate: formData.nextFollowUpDate || undefined,
        initialNote: formData.initialNote || undefined,
      })
      toast.success('Prospect created')
      setShowModal(false)
      resetForm()
      fetchData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create prospect')
    }
  }

  const handleStatusChange = async (prospectId: number, newStatus: ProspectStatus) => {
    try {
      await updateProspectStatus(prospectId, newStatus)
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus].label}`)
      fetchData()
      if (selectedProspect?.id === prospectId) {
        loadProspectDetail(prospectId)
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update status')
    }
  }

  const handleAddNote = async () => {
    if (!selectedProspect || !newNote.trim()) return
    setAddingNote(true)
    try {
      await addProspectNote(selectedProspect.id, newNote.trim())
      toast.success('Note added')
      setNewNote('')
      loadProspectDetail(selectedProspect.id)
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to add note')
    } finally {
      setAddingNote(false)
    }
  }

  const resetForm = () => {
    setFormData({
      companyName: '',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      address: '',
      website: '',
      services: 'website',
      socialMediaAdvertising: false,
      nextFollowUpDate: '',
      initialNote: '',
    })
  }

  const handleBulkImport = async () => {
    if (!importFile) return
    setImporting(true)
    setImportResult(null)
    try {
      const result = await bulkImportProspects(importFile, 'bulk_import')
      setImportResult(result)
      if (result.imported > 0) {
        toast.success(`Imported ${result.imported} prospects`)
        fetchData()
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to import prospects')
    } finally {
      setImporting(false)
    }
  }

  const resetBulkImport = () => {
    setImportFile(null)
    setImportResult(null)
    setShowBulkImportModal(false)
  }

  const downloadCsvTemplate = () => {
    const headers = 'company_name,contact_name,contact_phone,territory_code,trade_slot,contact_email,address,website,social_media_advertising,notes,services'
    const exampleRow1 = 'ABC Company,John Smith,07123456789,AB12,builder_slot,john@abc.com,123 High Street,www.oldsite.com,false,Needs new website,website'
    const exampleRow2 = 'XYZ Services,Jane Doe,07987654321,CD34,tree_surgeon_slot,jane@xyz.com,,www.xyz.co.uk,true,,website'
    const csvContent = `${headers}\n${exampleRow1}\n${exampleRow2}`

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'website_prospect_import_template.csv'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  const getFollowUpStatus = (date?: string) => {
    if (!date) return null
    const followUp = new Date(date)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const diff = Math.floor((followUp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

    if (diff < 0) return { label: `${Math.abs(diff)}d overdue`, class: 'text-red-400 bg-red-500/10' }
    if (diff === 0) return { label: 'Due today', class: 'text-amber-400 bg-amber-500/10' }
    if (diff <= 3) return { label: `In ${diff}d`, class: 'text-blue-400 bg-blue-500/10' }
    return null
  }

  // Count won prospects for earnings
  const wonCount = prospects.filter((p) => p.status === 'won').length

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 text-slate-500 animate-spin" />
      </div>
    )
  }

  return (
    <div className="relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-500/10">
            <Globe className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Website Sales</h1>
            <p className="text-slate-400">Manage website service prospects</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowBulkImportModal(true)} className="btn-secondary flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Bulk Import
          </button>
          <button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Prospect
          </button>
        </div>
      </div>

      {/* Commission Info */}
      <div className="card p-4 mb-6 bg-purple-500/5 border-purple-500/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PoundSterling className="w-5 h-5 text-purple-400" />
            <div>
              <p className="text-sm text-slate-400">Website Commission Rate</p>
              <p className="text-lg font-bold text-purple-400">£120 per sale</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-slate-400">Won This Month</p>
            <p className="text-2xl font-bold text-emerald-400">
              {wonCount} <span className="text-sm text-slate-500">= £{wonCount * 120}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-400">Filters:</span>
          </div>

          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Search prospects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-9 py-1.5 text-sm"
            />
          </div>

          {/* Status filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as ProspectStatus | 'all')}
            className="input py-1.5 px-3 text-sm w-auto"
          >
            <option value="all">All Statuses</option>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>
                {config.label}
              </option>
            ))}
          </select>

          {/* Clear filters */}
          {(filterStatus !== 'all' || searchQuery) && (
            <button
              onClick={() => {
                setFilterStatus('all')
                setSearchQuery('')
              }}
              className="text-sm text-slate-400 hover:text-slate-200 flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}

          <span className="text-sm text-slate-500 ml-auto">
            {filteredProspects.length} prospect{filteredProspects.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Prospects List */}
      {filteredProspects.length === 0 ? (
        <div className="card text-center py-12">
          <Building2 className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-200 mb-2">No prospects found</h3>
          <p className="text-slate-400 mb-4">
            {prospects.length === 0
              ? 'Get started by adding your first website prospect.'
              : 'Try adjusting your filters.'}
          </p>
          {prospects.length === 0 ? (
            <button onClick={() => setShowModal(true)} className="btn-primary">
              Add Prospect
            </button>
          ) : (
            <button
              onClick={() => {
                setFilterStatus('all')
                setSearchQuery('')
              }}
              className="btn-secondary"
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredProspects.map((prospect) => {
            const statusConfig = STATUS_CONFIG[prospect.status]
            const followUp = getFollowUpStatus(prospect.nextFollowUpDate)

            return (
              <div
                key={prospect.id}
                onClick={() => loadProspectDetail(prospect.id)}
                className="card p-4 cursor-pointer hover:border-slate-600/50 transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Company info */}
                    <div className="p-2 rounded-lg bg-purple-500/10">
                      <Globe className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-100">{prospect.companyName}</h3>
                      <div className="flex items-center gap-3 text-sm text-slate-400">
                        <span>{prospect.contactName}</span>
                        <span className="text-slate-600">|</span>
                        <span>{prospect.contactPhone}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Social Media Badge */}
                    {prospect.socialMediaAdvertising && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400">
                        Social Media
                      </span>
                    )}

                    {/* Follow-up badge */}
                    {followUp && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${followUp.class}`}>
                        {followUp.label}
                      </span>
                    )}

                    {/* Status badge */}
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium border ${statusConfig.bg} ${statusConfig.text} border-current/30`}
                    >
                      {statusConfig.label}
                    </span>

                    <ChevronRight className="w-5 h-5 text-slate-500" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Detail Panel (Slide-over) */}
      {showDetailPanel && selectedProspect && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => {
              setShowDetailPanel(false)
              setSelectedProspect(null)
              setSearchParams({})
            }}
          />
          <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-slate-900 border-l border-slate-700 z-50 overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white">{selectedProspect.companyName}</h2>
                  <p className="text-slate-400">{selectedProspect.contactName}</p>
                </div>
                <button
                  onClick={() => {
                    setShowDetailPanel(false)
                    setSelectedProspect(null)
                    setSearchParams({})
                  }}
                  className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              {/* Commission Info */}
              <div className="card p-4 mb-6 bg-purple-500/10 border-purple-500/20">
                <div className="flex items-center gap-2">
                  <PoundSterling className="w-5 h-5 text-purple-400" />
                  <div>
                    <p className="text-sm text-slate-400">Commission on conversion</p>
                    <p className="text-lg font-bold text-purple-400">£120 one-time</p>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="mb-6">
                <label className="label mb-2">Pipeline Status</label>
                <div className="flex flex-wrap gap-2">
                  {(['new', 'contacted', 'warm', 'in_discussion', 'won', 'not_interested', 'lost'] as ProspectStatus[]).map(
                    (status) => {
                      const config = STATUS_CONFIG[status]
                      const isActive = selectedProspect.status === status
                      return (
                        <button
                          key={status}
                          onClick={() => handleStatusChange(selectedProspect.id, status)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            isActive
                              ? `${config.bg} ${config.text} border border-current/50`
                              : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                          }`}
                        >
                          {config.label}
                        </button>
                      )
                    }
                  )}
                </div>
              </div>

              {/* Contact Info */}
              <div className="card p-4 mb-6">
                <h3 className="text-sm font-medium text-slate-300 mb-3">Contact Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-slate-500" />
                    <a href={`tel:${selectedProspect.contactPhone}`} className="text-blue-400 hover:underline">
                      {selectedProspect.contactPhone}
                    </a>
                  </div>
                  {selectedProspect.contactEmail && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-slate-500" />
                      <a href={`mailto:${selectedProspect.contactEmail}`} className="text-blue-400 hover:underline">
                        {selectedProspect.contactEmail}
                      </a>
                    </div>
                  )}
                  {selectedProspect.address && (
                    <div className="flex items-center gap-3">
                      <Building2 className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-300">{selectedProspect.address}</span>
                    </div>
                  )}
                  {selectedProspect.website && (
                    <div className="flex items-center gap-3">
                      <ExternalLink className="w-4 h-4 text-slate-500" />
                      <a
                        href={selectedProspect.website.startsWith('http') ? selectedProspect.website : `https://${selectedProspect.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:underline"
                      >
                        {selectedProspect.website}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Follow-up & Flags */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="card p-4">
                  <h3 className="text-sm font-medium text-slate-300 mb-2">Next Follow-up</h3>
                  {selectedProspect.nextFollowUpDate ? (
                    <>
                      <p className="text-white">{formatDate(selectedProspect.nextFollowUpDate)}</p>
                      {getFollowUpStatus(selectedProspect.nextFollowUpDate) && (
                        <p
                          className={`text-xs mt-1 ${
                            getFollowUpStatus(selectedProspect.nextFollowUpDate)!.class.replace('bg-', 'text-').split(' ')[0]
                          }`}
                        >
                          {getFollowUpStatus(selectedProspect.nextFollowUpDate)!.label}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-slate-500">Not scheduled</p>
                  )}
                </div>
                <div className="card p-4">
                  <h3 className="text-sm font-medium text-slate-300 mb-2">Social Media</h3>
                  {selectedProspect.socialMediaAdvertising ? (
                    <span className="px-2 py-1 rounded bg-blue-500/10 text-blue-400 text-sm">
                      Interested
                    </span>
                  ) : (
                    <p className="text-slate-500">Not interested</p>
                  )}
                </div>
              </div>

              {/* Notes Timeline */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Notes & Activity
                </h3>

                {/* Add note */}
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note..."
                    className="input flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleAddNote()
                      }
                    }}
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={addingNote || !newNote.trim()}
                    className="btn-primary px-3"
                  >
                    {addingNote ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>

                {/* Notes list */}
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {selectedProspect.notes.length === 0 ? (
                    <p className="text-slate-500 text-sm text-center py-4">No notes yet</p>
                  ) : (
                    selectedProspect.notes.map((note) => (
                      <div key={note.id} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
                        <p className="text-slate-200 text-sm">{note.content}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                          <span>{note.userName || 'Unknown'}</span>
                          <span>•</span>
                          <span>{formatDate(note.createdAt)}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Meta */}
              <div className="text-xs text-slate-500 border-t border-slate-700/50 pt-4">
                <p>Created: {formatDate(selectedProspect.createdAt)}</p>
                <p>Last updated: {formatDate(selectedProspect.updatedAt)}</p>
                {selectedProspect.lastContactedAt && <p>Last contacted: {formatDate(selectedProspect.lastContactedAt)}</p>}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-slate-100 mb-4">Add Website Prospect</h2>
            <form onSubmit={handleCreateProspect} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="label">Company Name *</label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">Contact Name *</label>
                  <input
                    type="text"
                    value={formData.contactName}
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">Contact Phone *</label>
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">Contact Email</label>
                  <input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">Current Website</label>
                  <input
                    type="text"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="input"
                    placeholder="www.example.com"
                  />
                </div>
                <div className="col-span-2">
                  <label className="label">Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              {/* Follow-up */}
              <div className="border-t border-slate-700/50 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Next Follow-up Date</label>
                    <input
                      type="date"
                      value={formData.nextFollowUpDate}
                      onChange={(e) => setFormData({ ...formData, nextFollowUpDate: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div className="flex items-center pt-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.socialMediaAdvertising}
                        onChange={(e) => setFormData({ ...formData, socialMediaAdvertising: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500/50"
                      />
                      <span className="text-sm text-slate-300">Interested in social media</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Initial Note */}
              <div>
                <label className="label">Initial Note</label>
                <textarea
                  value={formData.initialNote}
                  onChange={(e) => setFormData({ ...formData, initialNote: e.target.value })}
                  className="input"
                  rows={3}
                  placeholder="Add any initial notes about this prospect..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-700/50">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Prospect
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && (
        <div className="modal-overlay">
          <div className="modal-content p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-slate-100">Bulk Import Website Prospects</h2>
              <button onClick={resetBulkImport} className="p-2 rounded-lg hover:bg-slate-800">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            {!importResult ? (
              <>
                {/* Instructions */}
                <div className="card p-4 mb-4 bg-slate-800/50">
                  <h3 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4" />
                    CSV Format
                  </h3>
                  <p className="text-xs text-slate-400 mb-2">Required columns:</p>
                  <code className="text-xs text-blue-400 block bg-slate-900/50 p-2 rounded">
                    company_name, contact_name, contact_phone, territory_code, trade_slot
                  </code>
                  <p className="text-xs text-slate-400 mt-2 mb-1">Optional columns:</p>
                  <code className="text-xs text-slate-500 block bg-slate-900/50 p-2 rounded">
                    contact_email, address, website, social_media_advertising, notes
                  </code>
                  <p className="text-xs text-slate-500 mt-2">
                    Note: services will be set to "website" for all imported prospects.
                  </p>
                  <button
                    onClick={downloadCsvTemplate}
                    className="mt-3 text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                  >
                    <FileSpreadsheet className="w-3 h-3" />
                    Download CSV Template
                  </button>
                </div>

                {/* File Upload */}
                <div className="mb-4">
                  <label className="label mb-2">Select CSV File</label>
                  <div className="border-2 border-dashed border-slate-700 rounded-lg p-6 text-center hover:border-slate-600 transition-colors">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                      className="hidden"
                      id="csv-upload-website"
                    />
                    <label htmlFor="csv-upload-website" className="cursor-pointer">
                      {importFile ? (
                        <div className="flex items-center justify-center gap-2 text-slate-200">
                          <FileSpreadsheet className="w-6 h-6 text-green-400" />
                          <span>{importFile.name}</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                          <p className="text-slate-400">Click to select a CSV file</p>
                          <p className="text-xs text-slate-500 mt-1">or drag and drop</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button onClick={resetBulkImport} className="btn-secondary">
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkImport}
                    disabled={!importFile || importing}
                    className="btn-primary flex items-center gap-2"
                  >
                    {importing ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Import
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Import Results */}
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="card p-4 text-center">
                      <p className="text-2xl font-bold text-slate-100">{importResult.totalRows}</p>
                      <p className="text-xs text-slate-500">Total Rows</p>
                    </div>
                    <div className="card p-4 text-center bg-green-500/10">
                      <p className="text-2xl font-bold text-green-400">{importResult.imported}</p>
                      <p className="text-xs text-green-400/70">Imported</p>
                    </div>
                    <div className="card p-4 text-center bg-amber-500/10">
                      <p className="text-2xl font-bold text-amber-400">{importResult.skipped}</p>
                      <p className="text-xs text-amber-400/70">Skipped</p>
                    </div>
                  </div>

                  {/* Errors */}
                  {importResult.errors.length > 0 && (
                    <div className="card p-4 bg-red-500/10 border-red-500/30">
                      <h4 className="text-sm font-medium text-red-400 mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Errors ({importResult.errors.length})
                      </h4>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {importResult.errors.map((error, i) => (
                          <p key={i} className="text-xs text-red-300">{error}</p>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Success message */}
                  {importResult.imported > 0 && (
                    <div className="card p-4 bg-green-500/10 border-green-500/30 flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <p className="text-sm text-green-300">
                        Successfully imported {importResult.imported} prospect{importResult.imported !== 1 ? 's' : ''}
                      </p>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button onClick={resetBulkImport} className="btn-primary">
                      Done
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
